import { StyleSheet } from "@react-pdf/renderer";
import * as React from "react";
import { useSelector } from "react-redux";
import { Nullable } from "../../../types/utils";
import { OrderCategory, OrderCostingVersion, TApiOrderDetails } from "../../api/endpoints/Order";
import { selectUser } from "../../store/selectors/UserSelectors";
import { TApiSet } from "../../api/endpoints/Set";
import { selectParameters } from "../../store/selectors/new/parameters";
import { TApiProvider } from "../../api/endpoints/Provider";
import RealtimeOrderPDF from "./RealtimeOrderPDF";
import ArchiveOrderPDF from "./ArchiveOrderPDF";
import WebOrderPDF from "./WebOrderPDF";
import EuCutoutOrderPDF from "./EuCutoutOrderPDF";
import { TOrderProduct } from "../../api/endpoints/OrderProduct";
import { TApiServicePackage } from "../../api/endpoints/ServicePackage";
import { TApiVolumeBand } from "../../api/endpoints/VolumeBand";

interface IProps {
  order: TApiOrderDetails;
  currentCostingVersion: Nullable<OrderCostingVersion>;
  sets: Array<TApiSet>;
  provider?: TApiProvider;
  dataDeliverer?: TApiProvider;
  servicePackage?: TApiServicePackage;
  volumeBand?: TApiVolumeBand;
  products?: Array<TOrderProduct>;
}

const pending = "#ff7c00";
const accepted = "green";
const cancelled = "red";

const paddingXs = "2mm";
const paddingSm = "3mm";
const paddingMd = "5mm";
const paddingLg = "15mm";

const mainFontSize = "10pt";
const fontRegular = "Helvetica";
const fontBold = "Helvetica-Bold";
const fontItalic = "Helvetica-Oblique";

const lightFontColor = '#a7a7a7';
const grey = "#f0f0f0";

export const styles = StyleSheet.create({
  title: {
    fontFamily: fontBold,
    fontSize: "18pt",
    marginBottom: paddingSm
  },
  pageStyles: {
    fontFamily: fontRegular,
    fontSize: mainFontSize,
    padding: paddingLg
  },
  heading: {
    marginBottom: '10mm'
  },
  section: {
    marginTop: paddingLg
  },
  flexRow: {
    flexDirection: "row"
  },
  flexStretch: {
    justifyContent: "space-between"
  },
  alignCenter: {
    alignItems: "center"
  },
  alignTop: {
    alignItems: "flex-start"
  },
  flexCenter: {
    justifyContent: "center"
  },
  paddingBottomNormal: {
    paddingBottom: "8mm"
  },
  provider: {
    paddingRight: paddingMd,
    width: "50%"
  },
  statusNote: {
    width: "50%"
  },
  mainLogo: {
    width: "40mm"
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end"
  },
  footerTitles: {
    fontFamily: fontItalic,
    textAlign: "right",
    padding: paddingSm,
    width: "70%"
  },
  footerCosts: {
    width: "30%",
    textAlign: "right",
    padding: paddingSm
  },
  footerHighlight: {
    backgroundColor: grey,
    flexDirection: "row",
    justifyContent: "flex-end"
  },
  reference: {
    backgroundColor: grey,
    paddingTop: paddingSm,
    paddingBottom: paddingXs,
    paddingHorizontal: paddingMd
  },
  bold: {
    fontFamily: fontBold
  },
  sentence: {
    flexDirection: "row",
    alignItems: "center"
  },
  listItem: {
    marginBottom: paddingSm
  },
  pending: {
    borderColor: pending,
    color: pending
  },
  accepted: {
    borderColor: accepted,
    color: accepted
  },
  cancelled: {
    borderColor: cancelled,
    color: cancelled
  },
  status: {
    fontSize: '16pt',
    textTransform: 'uppercase'
  },
  noMargin: {
    marginVertical: 0,
    paddingVertical: 0
  },
  disclaimers: {
    paddingTop: paddingMd,
    paddingBottom: paddingMd,
    fontSize: "10pt"
  },
  disclaimer: {
    marginBottom: paddingMd
  },
  disclaimerContent: {
    paddingLeft: paddingXs
  },
  supText: {
    position: "relative",
    top: "-4pt",
    fontSize: "8pt",
    marginRight: "2pt"
  },
  ecQuoteText: {
    fontFamily: fontItalic,
    fontSize: "8pt",
    textAlign: "left",
    padding: paddingSm,
    paddingTop: paddingXs,
    width: '100%',
    color: lightFontColor
  }
});

/**
 * Shows a pdf summary of a single order.
 */
const OrderPDF = (props: IProps) => {
  const { order, currentCostingVersion, sets, provider, dataDeliverer, servicePackage, volumeBand, products } = props;
  const user = useSelector(selectUser);
  const availableParams = useSelector(selectParameters);

  const commonProps = {
    user,
    currentCostingVersion,
    sets,
    provider,
    dataDeliverer,
    servicePackage,
    volumeBand,
    availableParams,
    styles
  }

  if (order.category === OrderCategory.REALTIME) {
    return <RealtimeOrderPDF
      order={order as TApiOrderDetails<OrderCategory.REALTIME>}
      products={products as Array<TOrderProduct<OrderCategory.REALTIME>>}
      {...commonProps}
    />;
  } else if (order.category === OrderCategory.ARCHIVE) {
    return <ArchiveOrderPDF order={order as TApiOrderDetails<OrderCategory.ARCHIVE>} {...commonProps} />;
  } else if (order.category === OrderCategory.WEB) {
    return <WebOrderPDF order={order as TApiOrderDetails<OrderCategory.WEB>} {...commonProps} />;
  } else if (order.category === OrderCategory.EU_CUTOUT) {
    return <EuCutoutOrderPDF
      order={order as TApiOrderDetails<OrderCategory.EU_CUTOUT>}
      product={products?.[0] as TOrderProduct<OrderCategory.EU_CUTOUT>}
      {...commonProps}
    />;
  } else {
    return null;
  }
}

export default OrderPDF;
